import React, { useState, useEffect, useMemo } from "react";
import { useBookings } from "../../hooks/useBookings";
import { useVehicles } from "../../hooks/useVehicles";
import StatPill from "../../components/Admin/StatPill";
import BookingFilters from "../../components/Admin/BookingFilters";
import BookingsTable from "../../components/Admin/BookingsTable";
import VehicleTable from "../../components/Admin/VehicleTable";
import AddVehicleForm from "../../components/Admin/AddVehicleForm";
import EditVehicleModal from "../../components/Admin/EditVehicleModal";
import DayTripBookingsTable from "../../components/Admin/DayTripBookingsTable";
import AirportTransfersTable from "../../components/Admin/AirportTransfersTable";
import ThemeToggle from "../../components/Admin/ThemeToggle";
import Footer from "../../components/Customer/Footer";
import { DESTINATIONS } from "../../constants/destinations";
import { http } from "../../api/client";

export default function AdminDashboard() {
  // data hooks
  const {
    vehicles,
    loading: vehiclesLoading,
    err: vehiclesErr,
    createVehicle,
    updateVehicleById,
    deleteVehicleById,
  } = useVehicles();

  const {
    bookings,
    loading: bookingsLoading,
    err: bookingsErr,
    updateStatus,
    deleteById: deleteBookingById,
  } = useBookings();

  // State for day trip bookings and airport transfers
  const [dayTripBookings, setDayTripBookings] = useState([]);
  const [airportTransfers, setAirportTransfers] = useState([]);
  const [dayTripBookingsLoading, setDayTripBookingsLoading] = useState(false);
  const [airportTransfersLoading, setAirportTransfersLoading] = useState(false);
  const [dayTripBookingsErr, setDayTripBookingsErr] = useState(null);
  const [airportTransfersErr, setAirportTransfersErr] = useState(null);

  // Filters for day trip bookings and airport transfers
  const [dayTripFilterStatus, setDayTripFilterStatus] = useState("all");
  const [dayTripFilterDestination, setDayTripFilterDestination] = useState("all");
  const [airportTransferFilterStatus, setAirportTransferFilterStatus] = useState("all");
  const [airportTransferFilterType, setAirportTransferFilterType] = useState("all");

  // local UI state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editVehicle, setEditVehicle] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newVehicle, setNewVehicle] = useState({});

  const [filterStatus, setFilterStatus] = useState("All");
  const [filterVehicle, setFilterVehicle] = useState("All");
  const [dateOrder, setDateOrder] = useState("closest");

  // Fetch day trip bookings
  useEffect(() => {
    const fetchDayTripBookings = async () => {
      setDayTripBookingsLoading(true);
      try {
        const data = await http("/admin/day-trip-bookings");
        setDayTripBookings(Array.isArray(data) ? data : []);
      } catch (error) {
        setDayTripBookingsErr("Error fetching day trip bookings");
        setDayTripBookings([]);
      } finally {
        setDayTripBookingsLoading(false);
      }
    };

    fetchDayTripBookings();
  }, []);

  // Fetch airport transfers
  useEffect(() => {
    const fetchAirportTransfers = async () => {
      setAirportTransfersLoading(true);
      try {
        const data = await http("/admin/airport-transfers");
        setAirportTransfers(Array.isArray(data) ? data : []);
      } catch (error) {
        setAirportTransfersErr("Error fetching airport transfers");
        setAirportTransfers([]);
      } finally {
        setAirportTransfersLoading(false);
      }
    };

    fetchAirportTransfers();
  }, []);

  const loading = vehiclesLoading || bookingsLoading || dayTripBookingsLoading || airportTransfersLoading;
  const err = vehiclesErr || bookingsErr || dayTripBookingsErr || airportTransfersErr;

  // handlers
  const openEditVehicle = (v) => {
    setEditVehicle(v);
    setEditForm({
      model: v.model || "",
      year: v.year || "",
      type: v.type || "",
      price_per_day: v.price_per_day || "",
      image_url: v.image_url || "",
      seats: v.seats ?? v.number_of_seats ?? "",
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editVehicle) return;
    const payload = {
      model: editForm.model,
      year: Number(editForm.year),
      type: editForm.type,
      image_url: editForm.image_url,
      price_per_day: Number(editForm.price_per_day),
      seats: Number(editForm.seats),
    };
    await updateVehicleById(editVehicle.id, payload);
    setEditVehicle(null);
  };

  const addVehicle = async () => {
    const payload = {
      model: newVehicle.model,
      year: Number(newVehicle.year),
      type: newVehicle.type,
      image_url: newVehicle.image_url,
      price_per_day: Number(newVehicle.price_per_day),
      seats: Number(newVehicle.seats),
    };
    await createVehicle(payload);
    setNewVehicle({ model: "", year: "", type: "", price_per_day: "", image_url: "", seats: "" });
    setShowAddModal(false);
  };

  const deleteVehicle = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    await deleteVehicleById(id);
  };

  const updateBookingStatus = async (id, newStatus) => {
    await updateStatus(id, newStatus);
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    await deleteBookingById(id);
  };

  // Update day trip booking status
  const updateDayTripStatus = async (id, newStatus) => {
    try {
      await http(`/admin/day-trip-bookings/${id}/status`, {
        method: "PUT",
        body: JSON.stringify({ status: newStatus }),
      });
      setDayTripBookings(prev =>
        prev.map(booking => booking.id === id ? { ...booking, status: newStatus } : booking)
      );
    } catch (error) {
      console.error("Error updating day trip status:", error);
    }
  };

  // Delete day trip booking
  const deleteDayTrip = async (id) => {
    if (window.confirm("Are you sure you want to delete this day trip booking?")) {
      try {
        await http(`/admin/day-trip-bookings/${id}`, { method: "DELETE" });
        setDayTripBookings(prev => prev.filter(booking => booking.id !== id));
      } catch (error) {
        console.error("Error deleting day trip booking:", error);
      }
    }
  };

  // Update airport transfer status
  const updateAirportTransferStatus = async (id, newStatus) => {
    try {
      await http(`/admin/airport-transfers/${id}/status`, {
        method: "PUT",
        body: JSON.stringify({ status: newStatus }),
      });
      setAirportTransfers(prev =>
        prev.map(transfer => transfer.id === id ? { ...transfer, status: newStatus } : transfer)
      );
    } catch (error) {
      console.error("Error updating airport transfer status:", error);
    }
  };

  // Delete airport transfer
  const deleteAirportTransfer = async (id) => {
    if (window.confirm("Are you sure you want to delete this airport transfer?")) {
      try {
        await http(`/admin/airport-transfers/${id}`, { method: "DELETE" });
        setAirportTransfers(prev => prev.filter(transfer => transfer.id !== id));
      } catch (error) {
        console.error("Error deleting airport transfer:", error);
      }
    }
  };

  // derived
  const filteredSorted = useMemo(() => {
    const now = new Date();
    const byStatus = (b) => (filterStatus === "All" ? true : b.status === filterStatus);
    const byVehicle = (b) => (filterVehicle === "All" ? true : String(b.vehicle_id) === String(filterVehicle));

    return [...(bookings || [])]
      .filter(byStatus)
      .filter(byVehicle)
      .sort((a, b) => {
        const da = new Date(a.start_date);
        const db = new Date(b.start_date);
        const diffA = Math.abs(da - now);
        const diffB = Math.abs(db - now);
        return dateOrder === "closest" ? diffA - diffB : diffB - diffA;
      });
  }, [bookings, filterStatus, filterVehicle, dateOrder]);

  const top10 = filteredSorted.slice(0, 10);

  // Separate error handling for vehicle bookings
  const vehicleBookingsErr = bookingsErr || vehiclesErr;

  const counts = useMemo(() => {
    const total = (bookings || []).length;
    const pending = (bookings || []).filter((b) => b.status === "Pending").length;
    const confirmed = (bookings || []).filter((b) => b.status === "Confirmed").length;
    const completed = (bookings || []).filter((b) => b.status === "Completed").length;
    const cancelled = (bookings || []).filter((b) => b.status === "Cancelled").length;
    return { 
      total, 
      pending, 
      confirmed, 
      completed, 
      cancelled, 
      vehicles: (vehicles || []).length,
      dayTrips: (dayTripBookings || []).length,
      airportTransfers: (airportTransfers || []).length
    };
  }, [bookings, vehicles, dayTripBookings, airportTransfers]);

  // Filtered and sorted day trip bookings
  const filteredDayTrips = useMemo(() => {
    if (!dayTripBookings) return [];
    
    let filtered = dayTripBookings;
    
    if (dayTripFilterStatus !== "all") {
      filtered = filtered.filter(booking => booking.status === dayTripFilterStatus);
    }
    
    if (dayTripFilterDestination !== "all") {
      filtered = filtered.filter(booking => booking.destination === dayTripFilterDestination);
    }
    
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [dayTripBookings, dayTripFilterStatus, dayTripFilterDestination]);

  // Filtered and sorted airport transfers
  const filteredAirportTransfers = useMemo(() => {
    if (!airportTransfers) return [];
    
    let filtered = airportTransfers;
    
    if (airportTransferFilterStatus !== "all") {
      filtered = filtered.filter(transfer => transfer.status === airportTransferFilterStatus);
    }
    
    if (airportTransferFilterType !== "all") {
      filtered = filtered.filter(transfer => transfer.transfer_type === airportTransferFilterType);
    }
    
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [airportTransfers, airportTransferFilterStatus, airportTransferFilterType]);

  // Top 5 for dashboard display
  const top5DayTrips = filteredDayTrips.slice(0, 5);
  const top5AirportTransfers = filteredAirportTransfers.slice(0, 5);

  // render
  return (
    <div className="p-6 space-y-8 min-h-screen bg-gray-50 text-gray-900 dark:bg-slate-900 dark:text-gray-100">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-center gap-4">
          <img src="/images/araliya-tours-logo.png" alt="Araliya Tours" className="h-20 w-auto" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Manage bookings and vehicles. Use filters to find what you need fast.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/admin/bookings"
            className="inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-medium bg-blue-600 text-white shadow-sm hover:bg-blue-700"
          >
            View all bookings
          </a>
          <ThemeToggle />
        </div>
      </header>

        {/* Stats */}
        <section className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          <StatPill label="Total" value={counts.total} />
          <StatPill label="Pending" value={counts.pending} tone="amber" />
          <StatPill label="Confirmed" value={counts.confirmed} tone="blue" />
          <StatPill label="Completed" value={counts.completed} tone="emerald" />
          <StatPill label="Cancelled" value={counts.cancelled} tone="rose" />
          <StatPill label="Vehicles" value={counts.vehicles} tone="slate" />
          <StatPill label="Day Trips" value={counts.dayTrips} tone="purple" />
          <StatPill label="Airport" value={counts.airportTransfers} tone="indigo" />
        </section>

        {/* Vehicle Bookings card */}
        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div className="flex flex-col gap-3 p-5 border-b border-gray-100 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold">Vehicle Bookings</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Showing up to 10 by proximity to today</p>
            </div>
            <div className="w-full sm:w-auto">
              <BookingFilters
                vehicles={vehicles}
                filterStatus={filterStatus}
                setFilterStatus={setFilterStatus}
                filterVehicle={filterVehicle}
                setFilterVehicle={setFilterVehicle}
                dateOrder={dateOrder}
                setDateOrder={setDateOrder}
              />
            </div>
          </div>

          <div className="p-5">
            {loading ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
            ) : vehicleBookingsErr ? (
              <div className="text-sm text-rose-600">Error: {vehicleBookingsErr}</div>
            ) : (
              <>
                <BookingsTable
                  bookings={top10}
                  vehicles={vehicles}
                  onUpdateStatus={updateBookingStatus}
                  onDelete={deleteBooking}
                />
                {filteredSorted.length > 10 && (
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Showing 10 of {filteredSorted.length} result(s).{" "}
                    <a className="underline" href="/admin/bookings">
                      View all
                    </a>
                    .
                  </div>
                )}
              </>
            )}
          </div>

          {editVehicle && (
            <EditVehicleModal
              editForm={editForm}
              setEditForm={setEditForm}
              setEditVehicle={setEditVehicle}
              handleEditSubmit={handleEditSubmit}
            />
          )}
        </section>

        {/* Day Trip Bookings card */}
        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div className="flex flex-col gap-3 p-5 border-b border-gray-100 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold">Day Trip Bookings</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Tour and excursion requests</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-sm text-gray-500">
                {dayTripBookingsLoading ? "Loading..." : `${filteredDayTrips.length} requests`}
              </div>
              {/* Day Trip Filters */}
              <div className="flex gap-2">
                <select
                  value={dayTripFilterStatus}
                  onChange={(e) => setDayTripFilterStatus(e.target.value)}
                  className="text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white dark:bg-slate-700 dark:border-slate-600"
                >
                  <option value="all">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
                <select
                  value={dayTripFilterDestination}
                  onChange={(e) => setDayTripFilterDestination(e.target.value)}
                  className="text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white dark:bg-slate-700 dark:border-slate-600"
                >
                  <option value="all">All Destinations</option>
                  {DESTINATIONS.map((destination) => (
                    <option key={destination.key} value={destination.key}>
                      {destination.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="p-5">
            {dayTripBookingsLoading ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">Loading day trip bookings…</div>
            ) : dayTripBookingsErr ? (
              <div className="text-sm text-rose-600">Error: {dayTripBookingsErr}</div>
            ) : !dayTripBookings || dayTripBookings.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">No day trip bookings found.</div>
            ) : (
              <DayTripBookingsTable
                bookings={filteredDayTrips}
                onUpdateStatus={updateDayTripStatus}
                onDelete={deleteDayTrip}
              />
            )}
          </div>
        </section>

        {/* Airport Transfers card */}
        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div className="flex flex-col gap-3 p-5 border-b border-gray-100 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-semibold">Airport Transfers</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Pickup and dropoff requests</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-sm text-gray-500">
                {airportTransfersLoading ? "Loading..." : `${filteredAirportTransfers.length} requests`}
              </div>
              {/* Airport Transfer Filters */}
              <div className="flex gap-2">
                <select
                  value={airportTransferFilterStatus}
                  onChange={(e) => setAirportTransferFilterStatus(e.target.value)}
                  className="text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white dark:bg-slate-700 dark:border-slate-600"
                >
                  <option value="all">All Status</option>
                  <option value="Pending">Pending</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
                <select
                  value={airportTransferFilterType}
                  onChange={(e) => setAirportTransferFilterType(e.target.value)}
                  className="text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white dark:bg-slate-700 dark:border-slate-600"
                >
                  <option value="all">All Types</option>
                  <option value="pickup">Airport Pickup</option>
                  <option value="dropoff">Airport Dropoff</option>
                </select>
              </div>
            </div>
          </div>

          <div className="p-5">
            {airportTransfersLoading ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">Loading airport transfers…</div>
            ) : airportTransfersErr ? (
              <div className="text-sm text-rose-600">Error: {airportTransfersErr}</div>
            ) : !airportTransfers || airportTransfers.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">No airport transfer requests found.</div>
            ) : (
              <AirportTransfersTable
                transfers={filteredAirportTransfers}
                onUpdateStatus={updateAirportTransferStatus}
                onDelete={deleteAirportTransfer}
              />
            )}
          </div>
        </section>

        {/* Vehicles card */}
        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-slate-700">
            <div>
              <h2 className="text-xl font-semibold">Vehicles</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Manage your fleet and pricing</p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="rounded-xl px-4 py-2 text-sm font-medium bg-blue-600 text-white shadow-sm hover:bg-blue-700"
            >
              Add New Vehicle
            </button>
          </div>

          <div className="p-5">
            <VehicleTable vehicles={vehicles} openEditModal={openEditVehicle} onDelete={deleteVehicle} />
          </div>

          {showAddModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/50" onClick={() => setShowAddModal(false)} />
              <div className="relative z-10 w-full max-w-2xl rounded-2xl bg-white dark:bg-slate-800 p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Add New Vehicle</h3>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="px-2 py-1 rounded-lg bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-sm"
                  >
                    Close
                  </button>
                </div>
                <AddVehicleForm
                  newVehicle={newVehicle}
                  setNewVehicle={setNewVehicle}
                  addVehicle={addVehicle}
                />
              </div>
            </div>
          )}
        </section>
      <Footer />
    </div>
  );
}
